/*!
 * shape.js — shape(): a polygon made at run time
 * © 2026 Rob Canning — GPLv3
 *
 * Draws a shape into the score, where the performer is looking, so a score
 * can be built while it plays. One cue covers every regular polygon:
 *
 *     sides:0   circle            sides:3   triangle
 *     sides:1   line              sides:4   square
 *     sides:2   lens (two arcs)   sides:5…  pentagon and up
 *
 *     shape(sides:3, size:0.1, at:[0.3, 0.5], fill:#f60, stroke:#222, width:2)
 *     shape(uid:sh1, remove:1)        take it away (and its animations)
 *     shape(remove:all)               take every made shape away
 *     shape(n:12, ...) rotate(...)    a cluster: twelve, each rolled on its own
 *     shape(..., fill:scope) synth(...)   its sound drawn inside it (shapeLive.js)
 *     shape(..., fill:choose(black, white))  one of them, picked each run
 *     shape(m:5, n1:0.3, n2:0.3, n3:0.3)    a superformula: stars, flowers, blobs
 *     shape(uid:sh1, m:follow(bass.low, 2, 8))   ... its form moving with a sound
 *     shape(uid:sh1, sides:4, morph:2)     into a square over 2 beats (shapeForms.js)
 *     shape(..., fill:#fff, blend:difference)   overlapping shapes cut each other out
 *
 * A cluster line is expanded before it gets here (cueUtils expandCluster):
 * one shape() per member, uid:<id>_<i>, cluster:[<id>, <i>, <n>]. Members
 * live in <g id=<id> class="oscilla-shape-cluster">, which is what
 * shape(uid:<id>, remove:1) and viz(<id>, ...) reach; the last member of a
 * run takes away members past <n>, so a smaller n: shrinks the cluster.
 *
 * size: is the diameter as a fraction of the screen -- of the shorter side
 * of the band between the top bar and the transport, so size:1 just fits;
 * size:[w, h] stretches it, w of the band's width and h of its height (a
 * wide, flat triangle; a circle becomes an ellipse) -- and at: a point in
 * that band, 0..1 across and down. Both are turned into
 * score units when applied: from then on the shape belongs to the score and
 * scrolls and zooms with it.
 *
 * Running a shape() again with the same uid changes that shape in place --
 * sides, size, colour -- and leaves it where it is unless at: is given.
 *
 * The element is <g id=uid class="oscilla-shape"> around one <path>, so an
 * animation cue that follows it on the same line (cueDispatcher: a line led
 * by shape() hands its element to the rest) has a group to wrap and a clean
 * id to publish its signals under: shape(...) rotate(...) -> uid.angle.
 */

import { evalMaybeRandom, makeValueGenerator } from "./audio/audioShared.js";
import { stageBand, atPair } from "../system/viewBand.js";
import { resetAnimationsForElement } from "./animation.js";
import { syncLiveOverflow } from "../system/liveOverflow.js";
import { releaseO2P } from "./o2p.js";
import { publish } from "../control/paramBinding.js";
import { stopSound } from "./audio/audioFile.js";
import { destroyHitLabel } from "./animOverlays.js";
import { setShapeScope } from "./shapeLive.js";
import { formOf, formRadii, polygonRadii, mixRadii, radiiPath, ease, CIRCLE_FROM } from "./shapeForms.js";
import { isBindable, isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { secs } from "./beats.js";
import { blendOf } from "../system/vizStyle.js";
import { cellOf, cellPlacement, remember, forget } from "../system/stageGrid.js";
import { clearScatter } from "./scatter.js";
import { maybeConvertLink } from "../parser/parserSignalRef.js";
import { rectOnto, mapPoints, mapText } from "../system/mapWarp.js";
import { setMapped, dropMapped, setHandlesShown } from "../system/mapHandles.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const GROUP_ID = "oscilla-live-shapes";
const DEFAULTS = { sides: 3, size: 0.1, width: 2 };

let autoUid = 0;

export function scoreSvg() {
  return document.querySelector("#scrollStage svg")
    || document.querySelector("#scoreContainer svg");
}

/** The group made shapes go in, at the score's root. */
export function shapesGroup(svg) {
  let g = svg.getElementById(GROUP_ID);
  if (!g) {
    g = document.createElementNS(SVG_NS, "g");
    g.id = GROUP_ID;
    svg.appendChild(g);
  }
  return g;
}

/**
 * A pattern (Prand, Pseq ...) gives one value: a shape is drawn once a run
 * and has no dur: to step it by. A pattern that steps is color(vals:...,
 * dur:...) or another animation on the same line.
 */
let _patternHinted = false;
function once(v) {
  if (v?.type !== "pattern") return v;
  if (!_patternHinted) {
    _patternHinted = true;
    console.info("[shape] a pattern on shape() gives one value per run -- to step it, color(vals:prand(...), dur:...)");
  }
  let r = makeValueGenerator(v)?.next();
  if (Array.isArray(r)) r = r[Math.floor(Math.random() * r.length)];   // Pchoose([a, b])
  return r;
}

/**
 * What a shape is placed against: its group -- or, held by pin(), the hold's
 * wrapper, which carries the hold's offset: placed against the group, a held
 * shape run again with at:, or dragged, would jump by however far the score
 * had scrolled since the hold began.
 */
const placedIn = (el, group) => (el?.parentNode?.classList?.contains("pin-wrapper") ? el.parentNode : group);

// ---- at:, opacity:, fill: that move: a follow() or sinOsc() and its family -------

const MOVING_KEYS = ["at-x", "at-y", "opacity", "fill"];
const movingOwner = (uid, k) => `shape-moving:${uid}:${k}`;

/** Moved to (fx, fy) -- 0..1 of the screen: its centre, its pivot, its outline, its x / y on the bus. */
function moveShapeTo(el, group, uid, fx, fy) {
  const d = el.dataset;
  if (d.rx == null || !el.isConnected) return;
  const p = placement(placedIn(el, group), [fx, fy]);
  if (!p) return;
  // mapped: its points go with it
  if (d.map) shiftMap(el, group, uid, fx - Number(d.fx ?? fx), fy - Number(d.fy ?? fy));
  d.cx = p.cx; d.cy = p.cy; d.fx = fx; d.fy = fy;
  el.setAttribute("data-pivot", `${d.cx},${d.cy}`);
  for (const n of [el, ...el.querySelectorAll("*")]) {
    if (n.style?.transformOrigin) n.style.transformOrigin = `${d.cx}px ${d.cy}px`;
  }
  drawOutline(el);
  publish("shape", uid, { x: fx, y: fy });
}

/**
 * at:[sinOsc(0.25, 0, 0, 1), 0.5], at:[follow(bar.x, 0, 1), 0.3]: each moving
 * part bound, the shape moved as it moves. Returns at: as numbers now (the
 * moving parts at their values), for the placing; null when nothing moves.
 * A still at: lets go of what moved it before.
 */
function bindAt(el, group, uid, at) {
  // inside a list, follow(mouse.x) arrives as the call: a signal here, as on its own
  const parts = Array.isArray(at) ? at.map(maybeConvertLink) : null;
  if (!parts || !parts.some(isMoving)) {
    if (at != null) for (const k of ["at-x", "at-y"]) releaseBinding(movingOwner(uid, k));
    return null;
  }
  const now = [num(parts[0], 0.5), num(parts[1], 0.5)];
  ["at-x", "at-y"].forEach((k, i) => {
    if (!isMoving(parts[i])) { releaseBinding(movingOwner(uid, k)); return; }
    // a rangeless follow(mouse.x) is a place as it is (0..1), not 0..1 spread over -10..10
    const p = parts[i]?.type === "signalRef" && !parts[i].range && !parts[i].inRange ? { ...parts[i], range: [0, 1] } : parts[i];
    const b = bindParam(p, v => {
      const d = el.dataset;
      const fx = i === 0 ? v : Number(d.fx ?? now[0]), fy = i === 1 ? v : Number(d.fy ?? now[1]);
      moveShapeTo(el, group, uid, fx, fy);
    }, { owner: movingOwner(uid, k), min: -10, max: 10, default: 0.5 });
    if (Number.isFinite(b?.value)) now[i] = b.value;
  });
  return now;
}

// ---- drag:1 -- picked up and put down with the mouse -------------------------

/** 0..1 of the stage at a point on the screen (placement's other way round). */
function fractionAt(x, y) {
  const b = stageBand();
  return [(x - b.left) / (b.width || 1), (y - b.top) / (b.height || 1)];
}

/**
 * Where a drag has put a shape, as a line -- shape(uid:spot, dragTo:[x, y])
 * -- for the projector window to run (oscillaLive.js mirrorRun): it moves
 * there too, with no pointer showing over its stage. A dozen times a second
 * while it moves, and where it is let go.
 */
const _dragSent = new Map();          // uid -> when its move was last sent: each shape its own budget
function mirrorDrag(uid, x, y, last = false) {
  const now = performance.now();
  if (!last && now - (_dragSent.get(uid) ?? 0) < 80) return;
  _dragSent.set(uid, now);
  const expr = `shape(uid:${uid}, dragTo:[${+x.toFixed(4)}, ${+y.toFixed(4)}])`;
  window.dispatchEvent(new CustomEvent("oscilla:live-mirror", { detail: { expr, key: `drag:${uid}` } }));
}

/** Dragged here, by this window's pointer or the laptop's (dragTo:): moved, and kept there on a run again. */
function draggedTo(el, group, uid, x, y) {
  for (const k of ["at-x", "at-y"]) releaseBinding(movingOwner(uid, k));
  moveShapeTo(el, group, uid, x, y);
  el.dataset.dragged = "1";
}

/**
 * drag:1 -- the shape taken where it is pressed and moved with the pointer
 * (a mouse, a finger), kept where it is let go; at:'s follow() lets go of
 * it on the press. No pointer shows while it moves: the shape is where the
 * hand is. drag:0 fixed again, where it is.
 */
function setDrag(el, group, uid, on) {
  if (!on) {
    el._drag = null;
    delete el.dataset.drag;
    el.style.pointerEvents = el.style.cursor = el.style.touchAction = "";
    return;
  }
  el.dataset.drag = "1";
  // the inside too, and an unseen one (opacity:0): pressed anywhere on it
  el.style.pointerEvents = "all";
  el.style.cursor = "grab";
  el.style.touchAction = "none";
  if (el._drag) return;
  el._drag = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    // the fingers on it: a second one down makes the drag a pinch (pinchStart)
    const fingers = el._fingers ??= new Map();
    fingers.set(e.pointerId, [e.clientX, e.clientY]);
    try { el.setPointerCapture(e.pointerId); } catch { /* a pointer the page did not see go down */ }
    if (fingers.size === 2) { pinchStart(el); return; }
    if (fingers.size > 2) return;
    for (const k of ["at-x", "at-y"]) releaseBinding(movingOwner(uid, k));
    const [px, py] = fractionAt(e.clientX, e.clientY);
    const dx = px - Number(el.dataset.fx ?? 0.5), dy = py - Number(el.dataset.fy ?? 0.5);
    document.documentElement.classList.add("oscilla-dragging");
    let at = null, pinched = false;
    const move = (ev) => {
      if (fingers.has(ev.pointerId)) fingers.set(ev.pointerId, [ev.clientX, ev.clientY]);
      if (el._pinch) { pinched = true; pinchMove(el, group, uid); return; }
      // after a pinch, the finger left on it does not move it: it would jump
      if (pinched || ev.pointerId !== e.pointerId) return;
      const [x, y] = fractionAt(ev.clientX, ev.clientY);
      at = [x - dx, y - dy];
      draggedTo(el, group, uid, ...at);
      mirrorDrag(uid, ...at);
    };
    const up = (ev) => {
      fingers.delete(ev.pointerId);
      if (el._pinch && fingers.size < 2) pinchEnd(el, uid);
      if (fingers.size) return;
      document.documentElement.classList.remove("oscilla-dragging");
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      if (!at || pinched) return;
      mirrorDrag(uid, ...at, true);
      // where it was put down, into its line: a mapped one's points, or at:
      if (el.dataset.map) writeMap(uid, JSON.parse(el.dataset.map));
      else writeKey(uid, "at", `[${at.map(n => +n.toFixed(4)).join(", ")}]`);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };
  watchDrags();
}

/**
 * key:value written into the shape's line (or the viz() line saying it) --
 * oscillaLive.js writeKeyPlan. `plain`: not over a value with a call in it,
 * at:[follow(mouse.x), 0.5] is code, not a place.
 */
function writeKey(uid, key, value) {
  window.dispatchEvent(new CustomEvent("oscilla:write-key", { detail: { uid, key, value, cues: ["shape", "viz"], plain: true } }));
}

// ---- pinch -- a draggable shape sized by hand -----------------------------------
//
// Over a shape with drag:1: Ctrl+wheel (a trackpad's pinch comes as one) or
// two fingers on it. Its size about its middle, proportions kept; with Shift
// across only, Shift+Alt down only. Anywhere else a pinch zooms the score.
// The projector follows; when it stops, size: is written into the line.

/** size: as written: one number, or [w, h], three decimals. */
const sizeText = size => (Array.isArray(size) ? `[${size.map(n => +n.toFixed(3)).join(", ")}]` : String(+size.toFixed(3)));

/**
 * Its size `k` times what `from` was (d.size as kept, or a mapped one's
 * points about their middle), "both", "across" or "down".
 */
function resizeFrom(el, group, uid, from, k, way) {
  const d = el.dataset, kx = way === "down" ? 1 : k, ky = way === "across" ? 1 : k;
  if (from.map) {
    const pts = JSON.parse(from.map);
    const mx = pts.reduce((a, p) => a + p[0], 0) / pts.length, my = pts.reduce((a, p) => a + p[1], 0) / pts.length;
    d.map = JSON.stringify(pts.map(([x, y]) => [mx + (x - mx) * kx, my + (y - my) * ky]));
    drawOutline(el);
    handlesFor(el, group, uid);
    mirrorMap(uid, JSON.parse(d.map), false);
    return;
  }
  let size = JSON.parse(from.size);
  if (way === "both" && !Array.isArray(size)) size *= k;
  else {
    // one number is of the band's shorter side: as [w, h] of its width and height, to stretch one way
    if (!Array.isArray(size)) { const b = stageBand(), m = Math.min(b.width, b.height); size = [size * m / (b.width || 1), size * m / (b.height || 1)]; }
    size = [size[0] * kx, size[1] * ky];
  }
  const fit = n => Math.min(4, Math.max(0.005, n));
  size = Array.isArray(size) ? size.map(fit) : fit(size);
  d.size = JSON.stringify(size);
  const [wPx, hPx] = sizePx(size);
  d.rx = (wPx / 2) * Number(d.perPx); d.ry = (hPx / 2) * Number(d.perPx);
  drawOutline(el);
  mirrorSize(uid, size, false);
}

const _sizeSent = new Map();
function mirrorSize(uid, size, last) {
  const now = performance.now();
  if (!last && now - (_sizeSent.get(uid) ?? 0) < 80) return;
  _sizeSent.set(uid, now);
  window.dispatchEvent(new CustomEvent("oscilla:live-mirror", { detail: { expr: `shape(uid:${uid}, size:${sizeText(size)})`, key: `size:${uid}` } }));
}

/** Done sizing: the projector told where it ended, the line written. */
function sized(el, uid) {
  const d = el.dataset;
  if (d.map) { const pts = JSON.parse(d.map); mirrorMap(uid, pts, true); writeMap(uid, pts); return; }
  const size = JSON.parse(d.size);
  mirrorSize(uid, size, true);
  writeKey(uid, "size", sizeText(size));
}

const pinchable = el => el.dataset.form != null || !["vline", "hline"].includes(el.dataset.sides);
const fingerGap = el => { const [[ax, ay], [bx, by]] = [...el._fingers.values()]; return Math.hypot(bx - ax, by - ay) || 1; };

function pinchStart(el) {
  if (!pinchable(el)) return;
  el._pinch = { gap: fingerGap(el), from: { size: el.dataset.size, map: el.dataset.map } };
}
function pinchMove(el, group, uid) {
  if (el._fingers.size < 2) return;
  resizeFrom(el, group, uid, el._pinch.from, fingerGap(el) / el._pinch.gap, "both");
}
function pinchEnd(el, uid) {
  el._pinch = null;
  sized(el, uid);
}

/** Ctrl+wheel over a draggable shape: sized, not the score zoomed. */
const _wheelDone = new Map();          // uid -> the timer that writes its size, once the wheel stops
function wheelOnStage(e) {
  if (!(e.ctrlKey || e.metaKey)) return;
  const svg = scoreSvg();
  if (!svg?.contains(e.target)) return;
  const el = document.elementsFromPoint(e.clientX, e.clientY).map(n => n.closest?.(".oscilla-shape[data-drag]")).find(Boolean);
  if (!el || el.dataset.rx == null || !pinchable(el)) return;
  e.preventDefault();
  e.stopPropagation();
  const uid = el.id, group = shapesGroup(svg);
  // a mouse's notch is 100, a trackpad's pinch a few: no more than a quarter bigger at a time
  const dy = Math.max(-25, Math.min(25, e.deltaY || e.deltaX));
  const way = e.shiftKey ? (e.altKey ? "down" : "across") : "both";
  resizeFrom(el, group, uid, { size: el.dataset.size, map: el.dataset.map }, Math.exp(-dy * 0.01), way);
  clearTimeout(_wheelDone.get(uid));
  _wheelDone.set(uid, setTimeout(() => { _wheelDone.delete(uid); if (el.isConnected) sized(el, uid); }, 400));
}

/** While a finger is on a draggable shape, the score's own touch (its pinch zoom, its seek) is left out. */
function touchOnShape(e) {
  for (const t of e.touches || []) {
    const n = document.elementFromPoint(t.clientX, t.clientY);
    if (n?.closest?.(".oscilla-shape[data-drag]")?._fingers?.size) { e.stopPropagation(); return; }
  }
}

/** The stage's presses watched for drag:1 (a shape's, a sound's box's: viz.js), once. */
export function watchDrags() {
  if (!dragWatch) {
    document.addEventListener("pointerdown", pressOnStage, true);
    document.addEventListener("wheel", wheelOnStage, { capture: true, passive: false });
    for (const t of ["touchstart", "touchmove"]) document.addEventListener(t, touchOnShape, { capture: true, passive: true });
  }
  dragWatch = true;
}

/**
 * A press on the stage: the topmost shape or sound's box with drag:1 under
 * it is picked up, though something else on the stage is over it -- a
 * sound's box is drawn above the shapes (viz(s1, size:[1, 0.5]) covered the
 * spots), and its own scope over the box itself (the press went through to
 * the score, which scrolled). What is not the stage's -- the code, a text's
 * words, the bars -- keeps the press.
 */
let dragWatch = false;
function pressOnStage(e) {
  if (e.button !== 0) return;
  const svg = scoreSvg();
  if (!svg?.contains(e.target)) return;
  for (const n of document.elementsFromPoint(e.clientX, e.clientY)) {
    const shape = n.closest?.(".oscilla-shape[data-drag]");
    if (shape?._drag) { shape._drag(e); return; }
    if (n.dataset?.liveAudio != null && n.dataset.drag && n._dragStart) { n._dragStart(e); return; }
  }
}

/** A value as written: a number, rand(...), or a list for at:. */
function num(v, fallback) {
  const r = Number(evalMaybeRandom(once(v)));
  return Number.isFinite(r) ? r : fallback;
}

/** A word as written, or one picked by choose(a, b) / rand([a, b]) / Prand(...). */
function word(v) {
  const r = evalMaybeRandom(once(v));
  return r == null ? null : String(r);
}

/**
 * Screen -> score: the centre (from at:) and the scale (score units per
 * screen pixel), both read from the group's own screen CTM, which carries
 * the viewBox, the zoom and the scroll -- never from pixel sizes alone.
 */
export function placement(group, at) {
  const ctm = group.getScreenCTM();
  if (!ctm) return null;
  const inv = ctm.inverse();
  const b = stageBand();
  const fx = Array.isArray(at) ? num(at[0], 0.5) : 0.5;
  const fy = Array.isArray(at) ? num(at[1], 0.5) : 0.5;
  const p = new DOMPoint(b.left + fx * b.width, b.top + fy * b.height).matrixTransform(inv);
  return { cx: p.x, cy: p.y, perPx: Math.hypot(inv.a, inv.b) };
}

/**
 * A frame on the stage (an image()'s, a cam()'s): `rect` placed in `group`
 * with its middle at `at` and its size `size` -- fractions of the band, one
 * number for both or [w, h]. false when there is no screen to place by.
 */
export function placeFrame(rect, group, at, size) {
  const pl = placement(group, Array.isArray(at) ? at : [0.5, 0.5]);
  if (!pl) return false;
  const band = stageBand();
  const share = v => (Number(v) > 0 ? Number(v) : 1);
  const [fw, fh] = Array.isArray(size) ? size.map(share) : [share(size), share(size)];
  const w = fw * band.width * pl.perPx, h = fh * band.height * pl.perPx;
  rect.setAttribute("x", pl.cx - w / 2);
  rect.setAttribute("y", pl.cy - h / 2);
  rect.setAttribute("width", w);
  rect.setAttribute("height", h);
  return true;
}

/**
 * size: as screen px [w, h]: one number of the band's shorter side (a
 * regular shape stays regular), [w, h] of its width and height apart.
 */
export function sizePx(size) {
  const { width: bw, height: bh } = stageBand();
  if (Array.isArray(size)) return [size[0] * bw, size[1] * bh];
  return [size * Math.min(bw, bh), size * Math.min(bw, bh)];
}

/** sides: in words -- a rectangle its size exactly; a line the stage's height (vline) or width (hline). */
const RECTS = new Set(["rect", "bar", "vline", "hline"]);

/**
 * The outline of a regular shape of `sides` around (cx, cy), radius rx
 * across and ry down (equal for an unstretched one).
 */
function shapePath(sides, cx, cy, rx, ry = rx) {
  const f = n => +n.toFixed(2);
  // sides:rect (vline, hline): a rectangle exactly its size, rx and ry its half width and height
  if (RECTS.has(sides)) return `M ${f(cx - rx)} ${f(cy - ry)} H ${f(cx + rx)} V ${f(cy + ry)} H ${f(cx - rx)} Z`;
  if (sides >= CIRCLE_FROM || sides <= 0) {
    return `M ${f(cx - rx)} ${f(cy)} A ${f(rx)} ${f(ry)} 0 1 1 ${f(cx + rx)} ${f(cy)} A ${f(rx)} ${f(ry)} 0 1 1 ${f(cx - rx)} ${f(cy)} Z`;
  }
  if (sides === 1) return `M ${f(cx - rx)} ${f(cy)} L ${f(cx + rx)} ${f(cy)}`;
  if (sides === 2) {
    // flatter arcs than a circle: a lens, not a disc
    const Rx = rx * 1.3, Ry = ry * 1.3;
    return `M ${f(cx - rx)} ${f(cy)} A ${f(Rx)} ${f(Ry)} 0 0 1 ${f(cx + rx)} ${f(cy)} A ${f(Rx)} ${f(Ry)} 0 0 1 ${f(cx - rx)} ${f(cy)} Z`;
  }
  // a point at the top for odd counts, a flat top for even ones
  const a0 = -Math.PI / 2 + (sides % 2 === 0 ? Math.PI / sides : 0);
  const pts = [];
  for (let k = 0; k < sides; k++) {
    const a = a0 + (2 * Math.PI * k) / sides;
    pts.push(`${f(cx + rx * Math.cos(a))} ${f(cy + ry * Math.sin(a))}`);
  }
  return `M ${pts.join(" L ")} Z`;
}

// ---- superformula forms, and morphs between outlines (shapeForms.js) ----

const FORM_KEYS = ["m", "n1", "n2", "n3"];
const formOwner = (uid, k) => `shape-form:${uid}:${k}`;

/** Whether an outline can be said as radii: a form, a polygon, a circle -- not a line or a lens. */
const radial = d => d.form != null || !(Number(d.sides) === 1 || Number(d.sides) === 2 || RECTS.has(d.sides));

/** The outline's radii now (cached while the form or the sides stay the same). */
function outlineRadii(el) {
  const d = el.dataset, key = d.form ?? `sides:${d.sides}`;
  if (el._radiiKey !== key) {
    el._radii = d.form != null ? formRadii(formOf(JSON.parse(d.form))) : polygonRadii(Number(d.sides));
    el._radiiKey = key;
  }
  return el._radii;
}

/** Draw the outline from what the element keeps: its form or sides, centre and radii -- bent onto its map: points if it has them. */
function drawOutline(el) {
  if (el._morph) return;                         // the morph draws each frame
  const d = el.dataset, body = el.querySelector("path.oscilla-shape-body");
  if (!body) return;
  const [cx, cy, rx, ry] = [d.cx, d.cy, d.rx, d.ry].map(Number);
  body.setAttribute("d", d.form != null ? radiiPath(outlineRadii(el), cx, cy, rx, ry) : shapePath(RECTS.has(d.sides) ? d.sides : Number(d.sides), cx, cy, rx, ry));
  if (d.map) body.setAttribute("d", mappedPath(el, body));
}

// ---- map: -- bent onto points in the room (mapWarp.js; the handles: mapHandles.js) ----

/**
 * The flat outline as points, in score units: a polygon's, a rectangle's or
 * a line's corners (corners: true -- map:points can take them one by one), a
 * curve (a circle, a lens, a form) sampled along the path as drawn flat.
 */
function flatPoints(el, body) {
  const d = el.dataset;
  const [cx, cy, rx, ry] = [d.cx, d.cy, d.rx, d.ry].map(Number);
  if (d.form == null) {
    if (RECTS.has(d.sides)) return { pts: [[cx - rx, cy - ry], [cx + rx, cy - ry], [cx + rx, cy + ry], [cx - rx, cy + ry]], corners: true };
    const sides = Number(d.sides);
    if (sides === 1) return { pts: [[cx - rx, cy], [cx + rx, cy]], corners: true, open: true };
    if (sides >= 3 && sides < CIRCLE_FROM) {
      const a0 = -Math.PI / 2 + (sides % 2 === 0 ? Math.PI / sides : 0);
      return { pts: Array.from({ length: sides }, (_, k) => [cx + rx * Math.cos(a0 + (2 * Math.PI * k) / sides), cy + ry * Math.sin(a0 + (2 * Math.PI * k) / sides)]), corners: true };
    }
  }
  const len = body.getTotalLength?.() || 0;
  const N = 96;
  return { pts: Array.from({ length: N }, (_, i) => { const p = body.getPointAtLength((len * i) / N); return [p.x, p.y]; }), corners: false };
}

/** The outline's own bounds, [x, y, w, h]: a square's corners, not the circle it is drawn in. */
function boundsOf(pts) {
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return [x, y, Math.max(...xs) - x, Math.max(...ys) - y];
}

/** A point in score units as 0..1 of the stage, and back (placement). */
function toStage(group, [x, y]) {
  const ctm = group.getScreenCTM();
  if (!ctm) return [0.5, 0.5];
  const p = new DOMPoint(x, y).matrixTransform(ctm);
  return fractionAt(p.x, p.y);
}
function fromStage(group, f) {
  const p = placement(group, f);
  return p ? [p.cx, p.cy] : [0, 0];
}

/**
 * The outline bent onto map:'s points: four of them, the shape's box onto
 * them in perspective (rectOnto); one for each of a polygon's corners
 * (map:points), those corners there.
 */
function mappedPath(el, body) {
  const d = el.dataset, group = placedIn(el, el.closest(`#${GROUP_ID}`) ?? el.parentNode);
  const map = JSON.parse(d.map).map(p => fromStage(group, p));
  const flat = flatPoints(el, body);
  let pts;
  if (flat.corners && map.length === flat.pts.length && map.length !== 4) pts = map;
  else if (map.length === 4) {
    const f = rectOnto(boundsOf(flat.pts), map);
    pts = flat.pts.map(([x, y]) => f(x, y));
  } else return body.getAttribute("d");
  const r = n => +n.toFixed(2);
  return `M ${pts.map(([x, y]) => `${r(x)} ${r(y)}`).join(" L ")}${flat.open ? "" : " Z"}`;
}

/**
 * Where a handle's drag has put the points, as a line -- shape(uid:x, map:[...])
 * -- for the projector to run, a dozen times a second and where it is let go.
 */
const _mapSent = new Map();
function mirrorMap(uid, pts, last) {
  const now = performance.now();
  if (!last && now - (_mapSent.get(uid) ?? 0) < 80) return;
  _mapSent.set(uid, now);
  window.dispatchEvent(new CustomEvent("oscilla:live-mirror", { detail: { expr: `shape(uid:${uid}, map:${mapText(pts)})`, key: `map:${uid}` } }));
}

/** The fit, written into the shape's line (or the viz() line that mapped it): oscillaLive.js writes it. */
function writeMap(uid, pts) {
  window.dispatchEvent(new CustomEvent("oscilla:write-key", { detail: { uid, key: "map", value: mapText(pts), cues: ["shape", "viz"] } }));
}

/** Its handles, told where its points are; a drag on one bends it, mirrors it and, let go, writes it. */
function handlesFor(el, group, uid) {
  const pts = JSON.parse(el.dataset.map);
  setMapped(`shape:${uid}`, {
    points: pts,
    closed: !(el.dataset.form == null && Number(el.dataset.sides) === 1),
    onMove: (now, last, how) => {
      if (!el.isConnected) return;
      el.dataset.map = JSON.stringify(now);
      drawOutline(el);
      if (how?.resized) return;
      mirrorMap(uid, now, last);
      if (last) writeMap(uid, now);
    },
  });
}

/** Moved whole (a drag, at:'s follow()): its points by as much. */
function shiftMap(el, group, uid, dx, dy) {
  if (!dx && !dy) return;
  const pts = JSON.parse(el.dataset.map).map(([x, y]) => [x + dx, y + dy]);
  el.dataset.map = JSON.stringify(pts);
  handlesFor(el, group, uid);
}

const OFF = new Set(["0", "off", "false", "none"]);
let _mapHinted = false;

/**
 * map: -- map:1 four handles at its box's corners, map:points one at each
 * of a polygon's corners (mapping turned on: the handles show);
 * map:[[x, y], ...] the fit, written in by a drag (the handles as they
 * were); map:0 flat again.
 */
function setMap(el, group, uid, v) {
  const d = el.dataset;
  if (d.clusterIndex != null) {
    if (!_mapHinted) { _mapHinted = true; console.warn("[shape] map: is for one shape -- a cluster's members are each placed by the cluster"); }
    return;
  }
  if (OFF.has(String(v))) {
    delete d.map;
    dropMapped(`shape:${uid}`);
    drawOutline(el);
    return;
  }
  const given = mapPoints(v);
  if (given) d.map = JSON.stringify(given);
  else {
    const body = el.querySelector("path.oscilla-shape-body");
    const was = d.map;
    delete d.map;
    drawOutline(el);                               // flat, to read its points from
    const flat = flatPoints(el, body);
    // a line: its two ends (a box has no height to bend)
    const one = flat.corners && (String(v) === "points" || flat.open) ? flat.pts : null;
    if (String(v) === "points" && !one) console.warn(`[shape] map:points on ${uid} -- a curve has no corners to take: its box's four instead`);
    const want = one ? one.length : 4;
    const kept = was && JSON.parse(was).length === want ? JSON.parse(was) : null;
    const [bx, by, bw, bh] = boundsOf(flat.pts);
    const box = [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]];
    d.map = JSON.stringify(kept ?? (one ?? box).map(p => toStage(placedIn(el, group), p)));
    setHandlesShown(true);
  }
  drawOutline(el);
  handlesFor(el, group, uid);
}

/** Where the outline is now -- mid-morph, where the morph has got to. */
function outlineNow(el) {
  if (el._morph?.now) return el._morph.now;
  const d = el.dataset;
  return { radii: outlineRadii(el), cx: Number(d.cx), cy: Number(d.cy), rx: Number(d.rx), ry: Number(d.ry) };
}

/**
 * From `from` to what the element now keeps, over `ms`: the radii mixed
 * angle by angle, the centre and size gliding. What it keeps is read every
 * frame, so a follow() on the form goes on moving the target.
 */
function startMorph(el, from, ms) {
  if (el._morph) cancelAnimationFrame(el._morph.raf);
  const body = el.querySelector("path.oscilla-shape-body");
  const t0 = performance.now();
  const m = el._morph = { now: from, raf: 0 };
  const step = () => {
    if (el._morph !== m) return;
    const t = Math.min(1, (performance.now() - t0) / ms);
    if (t >= 1 || !el.isConnected) { el._morph = null; drawOutline(el); return; }
    const e = ease(t), d = el.dataset, L = (a, b) => a + (Number(b) - a) * e;
    m.now = { radii: mixRadii(from.radii, outlineRadii(el), e), cx: L(from.cx, d.cx), cy: L(from.cy, d.cy), rx: L(from.rx, d.rx), ry: L(from.ry, d.ry) };
    body.setAttribute("d", radiiPath(m.now.radii, m.now.cx, m.now.cy, m.now.rx, m.now.ry));
    m.raf = requestAnimationFrame(step);
  };
  step();
}

/**
 * m:, n1:, n2:, n3: as written: numbers, or follow(...) that keep the form
 * moving. A sides: with none of them makes it a polygon again.
 */
function setForm(el, uid, arg) {
  const d = el.dataset;
  const given = FORM_KEYS.filter(k => arg(k) != null);
  if (!given.length) {
    if (arg("sides") != null && d.form != null) {
      delete d.form;
      for (const k of FORM_KEYS) releaseBinding(formOwner(uid, k));
    }
    return;
  }
  const form = d.form != null ? JSON.parse(d.form) : {};
  for (const k of given) {
    const v = arg(k), owner = formOwner(uid, k);
    if (isBindable(v)) {
      const b = bindParam(v, x => {
        const f = d.form != null ? JSON.parse(d.form) : {};
        f[k] = x;
        d.form = JSON.stringify(f);
        drawOutline(el);
      }, { owner, default: 1 });
      form[k] = Number(b?.value ?? 1);
    } else {
      releaseBinding(owner);
      form[k] = num(v, 1);
    }
  }
  d.form = JSON.stringify(form);
}

/** The made shape -- or cluster -- with this uid, or null. Score elements are never touched. */
function findShape(uid) {
  const el = document.getElementById(uid);
  return el?.classList?.contains("oscilla-shape") || el?.classList?.contains("oscilla-shape-cluster") ? el : null;
}

/** The cluster's group, made on its first member. */
function clusterGroup(group, id) {
  let g = document.getElementById(id);
  if (g?.classList.contains("oscilla-shape-cluster")) return g;
  if (g) return null;                                   // a name already taken
  g = document.createElementNS(SVG_NS, "g");
  g.id = id;
  g.classList.add("oscilla-shape-cluster");
  group.appendChild(g);
  return g;
}

export function removeShape(el) {
  if (!el) return;
  stopSound(el.id);                    // a sound on its line was named after it
  if (el.classList.contains("oscilla-shape-cluster")) {
    for (const m of [...el.querySelectorAll(".oscilla-shape")]) removeShape(m);
    el.remove();
    return;
  }
  resetAnimationsForElement(el);
  releaseO2P(el);
  dropMapped(`shape:${el.id}`);
  for (const k of FORM_KEYS) releaseBinding(formOwner(el.id, k));
  for (const k of MOVING_KEYS) releaseBinding(movingOwner(el.id, k));
  if (el._morph) { cancelAnimationFrame(el._morph.raf); el._morph = null; }                      // a touch fader is not in the registry
  const reg = window.oscillaAnimRegistry || {};
  for (const [uid, entry] of Object.entries(reg)) {
    if (entry?.el === el || el.contains(entry?.el)) { destroyHitLabel(uid); delete reg[uid]; }
  }
  // an animation may have wrapped it: take the whole branch off its group
  // (the shapes layer, or the cluster it is a member of)
  const home = n => n?.id === GROUP_ID || n?.classList?.contains("oscilla-shape-cluster");
  let node = el;
  while (node.parentNode && !home(node.parentNode)) node = node.parentNode;
  node.remove();
}

/** shape(..., cell:4): the line with its cell made into at: and size: (null: no such cell, said why). */
function inGridCell(ast, uid, arg) {
  let v = arg("cell");
  if (v && typeof v === "object" && v.type === "funcCall") v = evalMaybeRandom(v);
  const { cell, why } = cellOf(v, uid, { makeGen: makeValueGenerator });
  if (cell == null) { console.warn(`[shape] ${why}`); return null; }
  const entry = remember(uid, { cell, across: num(arg("across"), 1), down: num(arg("down"), 1), size: arg("size"), at: arg("at") }, arg("cell"));
  const b = stageBand();
  const p = cellPlacement("shape", entry, { width: b.width, height: b.height });
  const args = ast.args.filter(a => !["cell", "across", "down", "at", "size"].includes(a.type));
  args.push({ type: "at", value: p.at }, { type: "size", value: p.size });
  return { ...ast, args };
}

/**
 * shape(...): make or change a shape. Returns its element, so the cues that
 * follow it on a line can act on it.
 */
export function handleShapeCue(ast) {
  // at:0.5 is at:[0.5, 0.5]
  const arg = name => { const v = ast?.args?.find(a => a.type === name)?.value; return name === "at" ? atPair(v) : v; };
  const svg = scoreSvg();
  if (!svg) { console.warn("[shape] no score to draw into"); return null; }

  const remove = arg("remove");
  if (remove != null) {
    if (String(remove) === "all") {
      // clusters whole (their group, and a trail in it, go too), then the rest
      for (const el of [...svg.querySelectorAll(".oscilla-shape-cluster")]) removeShape(el);
      for (const el of [...svg.querySelectorAll(".oscilla-shape")]) removeShape(el);
    } else {
      const el = findShape(String(arg("uid") ?? ""));
      if (el) removeShape(el);
    }
    syncLiveOverflow(svg);
    return null;
  }

  const uid = String(arg("uid") ?? `shape${++autoUid}`);
  // dragTo:[x, y] -- where a drag on the laptop put it (mirrorDrag): there, and nothing else
  const dragTo = arg("dragTo");
  if (Array.isArray(dragTo)) {
    const el = findShape(uid);
    if (el && el.dataset.rx != null) draggedTo(el, shapesGroup(svg), uid, num(dragTo[0], 0.5), num(dragTo[1], 0.5));
    return el ?? null;
  }
  // unset:[at, opacity, fill, blend, width] -- back to their defaults (a line run again
  // no longer saying them: liveEval.js resetsFor); a moving one lets go, staying where it is
  const unset = arg("unset");
  if (unset != null) {
    const el = findShape(uid);
    const list = (Array.isArray(unset) ? unset : [unset]).map(String);
    if (el) {
      if (list.includes("at")) for (const k of ["at-x", "at-y"]) releaseBinding(movingOwner(uid, k));
      if (list.includes("fill")) releaseBinding(movingOwner(uid, "fill"));
      if (list.includes("opacity")) { releaseBinding(movingOwner(uid, "opacity")); el.setAttribute("opacity", "1"); }
      if (list.includes("blend")) el.style.mixBlendMode = "";
      if (list.includes("width")) el.querySelector("path.oscilla-shape-body")?.setAttribute("stroke-width", String(DEFAULTS.width));
      if (list.includes("drag")) setDrag(el, shapesGroup(svg), uid, false);
      if (list.includes("map")) setMap(el, shapesGroup(svg), uid, 0);
    }
    // nothing else on the line: that is all
    if (!ast.args.some(a => !["uid", "unset", "_fromViz"].includes(a.type))) return el ?? null;
  }
  // cell: -- in a cell of the grid (stageGrid.js), as at: and size:
  if (arg("cell") != null) {
    ast = inGridCell(ast, uid, arg);
    if (!ast) return null;
  } else if (arg("at") != null && arg("_inCell") == null) forget(uid);   // put somewhere by hand: out of the grid
  const taken = document.getElementById(uid);
  if (taken && (!taken.classList.contains("oscilla-shape") || taken.classList.contains("oscilla-block"))) {
    console.warn(`[shape] uid:${uid} is already an element of the score — pick another name`);
    return null;
  }

  const group = shapesGroup(svg);
  // a member of a cluster: in the cluster's group, numbered
  const cl = arg("cluster");
  const cluster = Array.isArray(cl) && cl.length === 3
    ? { id: String(cl[0]), i: Number(cl[1]), n: Number(cl[2]) } : null;
  const home = cluster ? clusterGroup(group, cluster.id) : group;
  if (!home) {
    console.warn(`[shape] uid:${cluster.id} is already an element of the score — pick another name`);
    return null;
  }
  let el = taken;
  // its line run again says where it is: a scatter on it comes back (not viz()'s restyling)
  if (el && arg("_fromViz") == null) clearScatter(el);
  // an animation may have wrapped the path in its own group inside ours
  let body = el?.querySelector("path.oscilla-shape-body");
  if (!el) {
    el = document.createElementNS(SVG_NS, "g");
    el.id = uid;
    el.classList.add("oscilla-shape");
    body = document.createElementNS(SVG_NS, "path");
    body.classList.add("oscilla-shape-body");
    body.setAttribute("vector-effect", "non-scaling-stroke");
    el.appendChild(body);
    home.appendChild(el);
    if (cluster) el.dataset.clusterIndex = cluster.i;
    // placed by the screen, maybe left of where the page begins
    syncLiveOverflow(svg);
  }

  // Where and how big: kept from the last run unless asked again. Measured
  // against the group, so a shape an animation has wrapped is not measured
  // through its own moving transform.
  const d = el.dataset;
  // morph: beats -- from where the outline is now (a shape made before)
  const morph = taken && d.rx != null && radial(d) ? Number(secs(num(arg("morph"), 0))) : 0;
  const from = morph > 0 ? outlineNow(el) : null;
  // dragged (drag:1) and its line run again saying the same at: -- it stays
  // where it was put down; another at: (or none said before) places it anew
  // (a line without at: -- viz(spot, drag:1) -- does not move it at all)
  let keepDragged = false;
  if (arg("at") != null) {
    const atSrc = JSON.stringify(arg("at"));
    keepDragged = !!d.dragged && d.atSrc === atSrc && arg("_inCell") == null;
    if (!keepDragged) delete d.dragged;
    d.atSrc = atSrc;
  }
  // at: with a moving part: placed where it is now, then moved as it moves
  const at = keepDragged ? null : bindAt(el, group, uid, arg("at")) ?? arg("at");
  if (at != null || d.cx == null) {
    const p = placement(placedIn(el, group), at);
    if (!p) return null;
    d.cx = p.cx; d.cy = p.cy; d.perPx = p.perPx;
    // where it was put, as 0..1 of the screen: <uid>.x, <uid>.y for follow()
    d.fx = Array.isArray(at) ? num(at[0], 0.5) : 0.5;
    d.fy = Array.isArray(at) ? num(at[1], 0.5) : 0.5;
  }
  publish("shape", uid, { x: Number(d.fx ?? 0.5), y: Number(d.fy ?? 0.5) });
  // sides: a count, or a word: rect (bar) a rectangle its size exactly, vline / hline a line across the stage
  const sw = word(arg("sides"));
  const sides = RECTS.has(sw) ? sw : RECTS.has(d.sides) && arg("sides") == null ? d.sides
    : Math.round(num(arg("sides"), d.sides != null && !RECTS.has(d.sides) ? Number(d.sides) : DEFAULTS.sides));
  // size: as written -- one number, or [w, h] -- kept for the next run
  const sz = arg("size");
  let size = d.size != null ? JSON.parse(d.size) : DEFAULTS.size;
  if (Array.isArray(sz)) size = [num(sz[0], 0.1), num(sz[1] ?? sz[0], 0.1)];
  else if (sz != null) size = num(sz, DEFAULTS.size);
  // a line: the stage's whole height (width), size: its thickness -- 0.003 of the stage by default
  const thick = v => (Array.isArray(v) ? null : v);
  if (sides === "vline") size = [thick(sz != null ? num(sz, 0.003) : null) ?? (Array.isArray(size) && size[1] === 1 ? size[0] : 0.003), 1];
  if (sides === "hline") size = [1, thick(sz != null ? num(sz, 0.003) : null) ?? (Array.isArray(size) && size[0] === 1 ? size[1] : 0.003)];
  // a rectangle as wide and high as asked: one number is a square of the shorter side
  if (sides === "rect" || sides === "bar") { if (!Array.isArray(size)) size = [size, size]; }
  d.sides = sides; d.size = JSON.stringify(size);

  const [wPx, hPx] = sizePx(size);
  const rx = (wPx / 2) * Number(d.perPx), ry = (hPx / 2) * Number(d.perPx);
  d.rx = rx; d.ry = ry;
  // Turn about the shape's true centre: a triangle's bounding box is not
  // centred on it, and a line's has no height (rotate reads data-pivot).
  el.setAttribute("data-pivot", `${d.cx},${d.cy}`);
  // A rotate() already turning it read its pivot when it started; moved
  // (shape(at:), viz(at:)), the shape would orbit its old centre.
  for (const n of [el, ...el.querySelectorAll("*")]) {
    if (n.style?.transformOrigin) n.style.transformOrigin = `${d.cx}px ${d.cy}px`;
  }
  setForm(el, uid, arg);
  if (from && radial(d)) startMorph(el, from, morph * 1000);
  else { if (el._morph) { cancelAnimationFrame(el._morph.raf); el._morph = null; } drawOutline(el); }

  // night mode inverts the page (app.js); the shapes layer is turned back
  const dark = document.documentElement.style.filter.includes("invert")
    || document.body.classList.contains("dark-mode");
  const ink = dark ? "#eeeeee" : "#222222";
  // fill:scope -- its sound drawn inside it, the shape itself left open
  // fill: moving -- a grey from black (0) to white (1), as the signal says
  const fillRaw = arg("fill");
  if (isMoving(fillRaw)) {
    bindParam(fillRaw, v => { const g = Math.round(Math.min(1, Math.max(0, v)) * 255); body.setAttribute("fill", `rgb(${g}, ${g}, ${g})`); },
      { owner: movingOwner(uid, "fill"), min: 0, max: 1, default: 1 });
  } else if (fillRaw != null) releaseBinding(movingOwner(uid, "fill"));
  const fill = isMoving(fillRaw) ? null : word(fillRaw);
  if (fill != null) setShapeScope(el, fill === "scope");
  // a line (vline, hline) solid in the ink, with no edge, unless told
  const lineShape = sides === "vline" || sides === "hline";
  body.setAttribute("fill", d.scopeFill ? "none" : fill ?? body.getAttribute("fill") ?? (lineShape ? ink : "none"));
  const stroke = word(arg("stroke"));
  body.setAttribute("stroke", stroke ?? body.getAttribute("stroke") ?? (lineShape ? "none" : ink));
  // a colour written now replaces one a color() left on it
  if (fill != null) body.style.fill = "";
  if (stroke != null) body.style.stroke = "";
  body.setAttribute("stroke-width", String(num(arg("width"), Number(body.getAttribute("stroke-width")) || DEFAULTS.width)));
  const opacity = arg("opacity");
  if (isMoving(opacity)) bindParam(opacity, v => el.setAttribute("opacity", String(v)), { owner: movingOwner(uid, "opacity"), min: 0, max: 1, default: 1 });
  else if (opacity != null) { releaseBinding(movingOwner(uid, "opacity")); el.setAttribute("opacity", String(num(opacity, 1))); }
  // blend:difference -- mixed with the shapes under it (vizStyle blendOf)
  const blend = arg("blend");
  if (blend != null) { const b = blendOf(word(blend)); if (b != null) el.style.mixBlendMode = b; }
  // drag:1 -- moved by hand (setDrag)
  const drag = arg("drag");
  if (drag != null) setDrag(el, group, uid, !OFF.has(String(drag)));
  // map:1 / map:points / map:[[x, y] ...] / map:0 -- bent onto points in the room (setMap)
  const map = arg("map");
  if (map != null) setMap(el, group, uid, map);
  else if (el.dataset.map) handlesFor(el, group, uid);     // run again: the fit kept, its handles where it is

  // the last member of a run: members past n (a smaller n: than before) go
  if (cluster && cluster.i === cluster.n) {
    for (const m of [...home.querySelectorAll(":scope .oscilla-shape")]) {
      if (Number(m.dataset.clusterIndex) > cluster.n) removeShape(m);
    }
  }
  return el;
}
